import { useState } from "react";
import { MapPin, X, Navigation } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * IpLocationBanner — subtle banner shown when using IP-derived location.
 * Displays: "Showing results near [City]. Enable location for more accurate results."
 * Dismissible per session.
 */
export default function IpLocationBanner() {
  const { geo } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const ipLoc = geo?.ipLocation;
  const isUsingIp = geo?.permission === "denied" && ipLoc?.city;

  if (!isUsingIp || dismissed) return null;

  const cityLabel = [ipLoc.city, ipLoc.region_code || ipLoc.region]
    .filter(Boolean)
    .join(", ");

  const handleEnableLocation = () => {
    setDismissed(true);
    geo?.requestLocation?.();
  };

  return (
    <div
      className="relative flex items-center gap-2.5 px-4 py-2.5 bg-amber-50 border-b border-amber-200/60"
      data-testid="ip-location-banner"
    >
      <MapPin className="w-3.5 h-3.5 text-amber-600 shrink-0" />
      <p className="text-[11px] text-amber-800 flex-1 leading-snug">
        Showing results near <strong>{cityLabel}</strong>.{" "}
        <button
          onClick={handleEnableLocation}
          className="inline-flex items-center gap-0.5 text-amber-600 font-semibold hover:underline"
          data-testid="enable-location-link"
        >
          <Navigation className="w-3 h-3" />
          Enable location
        </button>{" "}
        for more accurate results.
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 text-amber-400 hover:text-amber-600 transition shrink-0"
        data-testid="ip-banner-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
